import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import ViewAllButton from 'src/components/ViewAllButton';
import { PERMISSION } from 'src/features/auth';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { HomeStatistics, PAST_STAYS_PANEL_NAME, StatisticEntry } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import StaySidePanel from 'src/features/visitor/components/StaySidePanel';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Translation } from 'src/types';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';

type Props = {
    entries?: HomeStatistics['entries'];
    isError?: boolean;
};
export const STAYS_PANEL_NAME = 'stays-side-panel';

export default function WidgetEntries({ entries, isError }: Props) {
    const { formatMessage, locale } = useIntl();

    const translate = React.useCallback(
        (translations?: Translation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );
    const [selectedStay, setSelectedStay] = React.useState<{ id: string; isActive: boolean } | null>(null);

    const { clearSelection, setRowSelectionState } = useSingleRowSelection();
    const { openPanel, closePanel } = useSidePanel();

    const singleRowSelection = React.useCallback(
        (rowId: string) => setRowSelectionState({ [rowId]: true }),
        [setRowSelectionState],
    );

    const permissionAccessVisitor = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const permissionAccessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);

    const columns: ColumnDef<StatisticEntry>[] = [
        {
            accessorKey: 'visitor',
            header: formatMessage(homeMessages.visitor),
            cell: ({ row }) => {
                const entry = row.original;
                const visitor = entry.visitor;
                const isActive = Boolean(entry.active_stay);
                const stayId = entry?.active_stay?.id || entry?.past_stay?.id || '';
                const handleClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    clearSelection();
                    closePanel(PAST_STAYS_PANEL_NAME);
                    singleRowSelection(stayId);
                    setSelectedStay({ id: stayId, isActive });
                    openPanel(STAYS_PANEL_NAME);
                };

                return (
                    <div
                        onClick={permissionAccessVisitor ? handleClick : undefined}
                        className={cn(
                            permissionAccessVisitor &&
                                'text-primary-700 hover:text-primary-600 cursor-pointer font-semibold',
                        )}
                    >
                        {visitor?.main_identifier?.value}
                    </div>
                );
            },
        },
        {
            accessorKey: 'passed_on',
            header: formatMessage(homeMessages.passedOn),
            cell: (info) =>
                new Date(info.getValue() as string).toLocaleTimeString(locale, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                }),
        },
        {
            accessorKey: 'entry_gateway',
            header: formatMessage(gatewayMessages.gateway),
            cell: (info) => {
                const entryGateway = info.getValue() as StatisticEntry['entry_gateway'];

                return translate(entryGateway.translations);
            },
        },
    ];

    return (
        <GeneralBlock className="min-w-0 flex-1 gap-6">
            <div className="flex items-start justify-between">
                <div className="flex gap-6">
                    <div className="flex size-14 items-center justify-center rounded-full border-8 border-green-50 bg-green-100 p-1 text-2xl text-green-700">
                        <FontAwesomeIcon icon={ICON_MAP.TREND_UP} />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-600">
                            {formatMessage(homeMessages.totalEntriesToday)}
                        </span>
                        <span className="text-lg font-bold">{isError ? '-' : entries?.total_entries}</span>
                    </div>
                </div>
                {permissionAccessActiveStays && <ViewAllButton route={`/${PATH.VISITORS}/gateway-passes`} />}
            </div>

            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                <>
                    <div className="w-full overflow-x-auto">
                        <StatisticTable
                            className="w-full"
                            title={formatMessage(homeMessages.lastTenEntries)}
                            data={entries?.last_entries}
                            columns={columns as any}
                        />
                    </div>
                    <SidePanel onClose={clearSelection} name={STAYS_PANEL_NAME}>
                        <StaySidePanel
                            showEntryFirst
                            isActiveStay={selectedStay?.isActive ?? true}
                            selectedStayId={selectedStay?.id}
                        />
                    </SidePanel>
                </>
            )}
        </GeneralBlock>
    );
}
